import { Box, Theme } from '@mui/material'
import { useEffect, useMemo, useRef } from 'react'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { blue, gray } from '~/common/styles/colors'
import { PaginatedComment } from '~/components/activity/types'
import { cn } from '../../../@/lib/utils'
import { useGlobalContext } from '../../../common/hooks/useGlobalContext'
import { usePage } from '../../../common/hooks/usePage'
import { UserAvatar } from '../../users/UserAvatar'
import { CommentContent } from '../comment/CommentContent'
import { CommentField } from '../comment/CommentField'
import { ChildComment } from './ChildComment'

function getStyles(theme: Theme) {
  return {
    commentText: {
      border: `1px solid ${gray[5]}`,
      borderRadius: '2px',
      color: gray[10],
      paddingBlock: theme.spacing(1),
      paddingInline: theme.spacing(1.5),
    },
    childCommentText: {
      marginBottom: theme.spacing(1),
    },
    commentInput: {
      '& .MuiInputBase-root': {
        alignItems: 'center',
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: theme.palette.common.doss.gray[5],
        },
        '&:hover fieldset': {
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.palette.primary.main,
        },
      },
    },
    childComment: {
      paddingTop: theme.spacing(1),
      display: 'flex',
      body: {
        flexGrow: 1,
      },
    },
    textButton: {
      paddingTop: theme.spacing(1),
      color: blue[9],
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
      indented: {
        paddingInlineStart: theme.spacing(4),
      },
    },
    subCommentIcon: {
      color: gray[7],
      marginInlineEnd: theme.spacing(0.5),
    },
    replyHeader: {
      paddingInlineEnd: theme.spacing(1),
    },
  }
}

interface ActivityCommentProps {
  comment: PaginatedComment
}

export function ActivityComment({ comment }: ActivityCommentProps) {
  const { t } = useTypedTranslation()
  const styles = useStyles(getStyles)
  const ref = useRef<HTMLDivElement>(null)
  const { objectId, objectType, query } = usePage()
  const { user: currentUser } = useGlobalContext()
  const highlightedCommentId = useMemo(() => query.commentId || '', [query.commentId])

  const isHighlighted = useMemo(
    () => highlightedCommentId === comment.comment.id,
    [comment.comment.id, highlightedCommentId],
  )

  const parentCommentId = useMemo(() => comment.comment.id, [comment.comment.id])

  useEffect(() => {
    if (isHighlighted) {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const timeline = ref.current
        setTimeout(() => timeline.classList.add('bg-white'), 3000)
      }
    }
  }, [isHighlighted])

  if (!currentUser) {
    return null
  }

  const hasReplies = comment.childComments.length > 0
  return (
    <>
      {/* Parent comment */}
      <div
        ref={ref}
        className={cn(
          'flex py-1 text-sm px-2 -mx-2 rounded',
          isHighlighted && 'bg-blue-2 transition-colors duration-[2000ms]',
        )}
      >
        <div>
          <CommentContent comment={comment.comment} />
        </div>
      </div>
      <div className="my-3 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap">
        <span className="text-gray-9">
          {t('activity.comment.replies', {
            count: comment.childComments.length,
          })}
        </span>
        <div className="h-px w-full flex-1 border-t" />
      </div>
      {/* Child comments */}
      {hasReplies && (
        <div>
          <Box sx={styles.childComment.body}>
            {comment.childComments.map((childComment) => (
              <ChildComment key={childComment.comment.id} comment={childComment.comment} />
            ))}
            <div className="flex items-start justify-between gap-1">
              <div className="flex items-center pt-1.5">
                <UserAvatar user={currentUser} />
              </div>
              <div className="w-full flex-1 items-center p-1">
                <CommentField
                  objectId={objectId}
                  objectType={objectType}
                  parentCommentId={parentCommentId}
                  placeholder={t('activity.comment.reply_placeholder')}
                />
              </div>
            </div>
          </Box>
        </div>
      )}
      {!hasReplies && (
        <div className="flex items-start justify-between gap-1">
          <div className="flex items-center pt-1.5">
            <UserAvatar user={currentUser} />
          </div>
          <div className="w-full flex-1 items-center p-1">
            <CommentField
              objectId={objectId}
              objectType={objectType}
              parentCommentId={parentCommentId}
              placeholder={t('activity.comment.reply_placeholder')}
            />
          </div>
        </div>
      )}
    </>
  )
}
